import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { imgSrc } from '../lib/imgSrc';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import PartsPicker from './PartsPicker';
import { SerializedWorkSummary } from './SerializedWorkSummary';
import InstallationPointMultiSelect from './InstallationPointMultiSelect';
import { parsePartsUsedString } from '../lib/partsUsed';

const EditMaintenanceDialog = ({ open, onClose, log, installationPoints = [], onSuccess }) => {
  const [formData, setFormData] = useState({
    work_description: '',
    resolution: '',
    hours_spent: '',
    work_date: '',
    follow_up_required: '',
    notes: ''
  });
  const [installationPointIds, setInstallationPointIds] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    if (log) {
      let workDateFormatted = '';
      if (log.work_date) {
        try {
          // The datetime-local <input> takes a LOCAL wall-clock time
          // (YYYY-MM-DDTHH:MM). Building it via toISOString() feeds UTC
          // into a local-time input, which then round-trips back to UTC
          // on save — resulting in the log's timestamp walking by the
          // user's timezone offset every edit. Build the string from
          // local components instead.
          const d = new Date(log.work_date);
          if (!Number.isNaN(d.getTime())) {
            const pad = (n) => String(n).padStart(2, '0');
            workDateFormatted =
              `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
              `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          }
        } catch (e) {
          console.error('Error parsing work_date:', e);
        }
      }

      setFormData({
        work_description: log.work_description || '',
        resolution: log.resolution || '',
        hours_spent: log.hours_spent ?? '',
        work_date: workDateFormatted,
        follow_up_required: log.follow_up_required || '',
        notes: log.notes || ''
      });
      setInstallationPointIds(log.installation_point_ids || (log.installation_point_id ? [log.installation_point_id] : []));
      setExistingImages(log.images || []);
      setImagePreviews([]);
      setImageFiles([]);

      // Rehydrate parts picker from the log's parts_used string.
      // Existing parts (that match inventory) are populated from the API;
      // unknown tokens fall through as pseudo "new" placeholders (no auto-add on save).
      (async () => {
        const tokens = parsePartsUsedString(log.parts_used || '');
        if (tokens.length === 0) {
          setSelectedParts([]);
          return;
        }
        try {
          const invRes = await axios.get(`${API}/inventory`);
          const byPart = new Map((invRes.data || []).map((it) => [it.part_number, it]));
          setSelectedParts(
            tokens.map((t) => {
              const match = byPart.get(t.part_number);
              if (match) {
                return {
                  part_number: match.part_number,
                  name: match.name,
                  qty: t.qty,
                  existingQty: match.quantity ?? 0,
                };
              }
              return {
                part_number: t.part_number,
                name: t.part_number,
                qty: t.qty,
                isUnknown: true,
              };
            })
          );
        } catch (e) {
          setSelectedParts([]);
        }
      })();
    }
  }, [log]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);

    const previews = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then(setImagePreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const newImageUrls = [];
      for (const file of imageFiles) {
        const formDataImage = new FormData();
        formDataImage.append('file', file);
        const uploadRes = await axios.post(`${API}/upload-image`, formDataImage);
        newImageUrls.push(uploadRes.data.image_url);
      }

      const allImages = [...existingImages, ...newImageUrls];

      let workDateISO = null;
      if (formData.work_date) {
        workDateISO = new Date(formData.work_date).toISOString();
      }

      const partsUsedString = selectedParts
        .map((p) => (p.qty > 1 ? `${p.part_number} x${p.qty}` : p.part_number))
        .join(', ');

      const submitData = {
        work_description: formData.work_description,
        resolution: formData.resolution || null,
        hours_spent: parseFloat(formData.hours_spent),
        parts_used: partsUsedString,
        work_date: workDateISO,
        follow_up_required: formData.follow_up_required || null,
        notes: formData.notes || null,
        images: allImages,
        installation_point_ids: installationPointIds,
      };

      await axios.put(`${API}/maintenance-logs/${log.id}`, submitData);
      toast.success('Maintenance log updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update maintenance log');
    } finally {
      setUploading(false);
    }
  };

  const removeExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Maintenance Log</DialogTitle>
          <DialogDescription>Update maintenance work details</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <SerializedWorkSummary records={log?.serialized_work} testId="edit-log-serialized-work" />
          <div className="space-y-2">
            <Label htmlFor="edit-work-description">Work Description/Issue *</Label>
            <Textarea
              id="edit-work-description"
              data-testid="edit-work-description-input"
              value={formData.work_description}
              onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
              placeholder="Describe the issue or work performed"
              rows={3}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-resolution">Resolution</Label>
            <Textarea
              id="edit-resolution"
              data-testid="edit-resolution-input"
              value={formData.resolution}
              onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
              placeholder="Describe how the issue was resolved"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-hours-spent">Hours Spent *</Label>
              <Input
                id="edit-hours-spent"
                data-testid="edit-hours-spent-input"
                type="number"
                step="0.1"
                value={formData.hours_spent}
                onChange={(e) => setFormData({ ...formData, hours_spent: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-work-date">Work Date & Time</Label>
              <Input
                id="edit-work-date"
                data-testid="edit-work-date-input"
                type="datetime-local"
                value={formData.work_date}
                onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
              />
            </div>
          </div>

          {installationPoints.length > 0 && (
            <div className="space-y-2">
              <Label>Installation Points (optional)</Label>
              <InstallationPointMultiSelect
                points={installationPoints}
                value={installationPointIds}
                onChange={setInstallationPointIds}
                testIdPrefix="edit-log-ip"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Parts Used</Label>
            <PartsPicker
              value={selectedParts}
              onChange={setSelectedParts}
              disabled={uploading}
            />
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Inventory is <span className="font-medium">not</span> auto-deducted on edit.
              Adjust stock manually from the Inventory page if you change which parts were used.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-follow-up">Follow-up Action Required</Label>
            <Textarea
              id="edit-follow-up"
              data-testid="edit-follow-up-textarea"
              value={formData.follow_up_required}
              onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.value })}
              placeholder="Describe any follow-up actions needed (optional)"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Additional Notes</Label>
            <Textarea
              id="edit-notes"
              data-testid="edit-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional observations or issues found"
              rows={3}
            />
          </div>

          {existingImages.length > 0 && (
            <div className="space-y-2">
              <Label>Existing Images</Label>
              <div className="grid grid-cols-3 gap-2">
                {existingImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img src={imgSrc(img)} alt={`Existing ${idx + 1}`} className="h-24 rounded object-cover w-full" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => removeExistingImage(idx)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-images">Add More Images (Optional)</Label>
            <Input
              id="edit-images"
              data-testid="edit-images-input"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
            />
            {imagePreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {imagePreviews.map((preview, idx) => (
                  <img key={idx} src={preview} alt={`Preview ${idx + 1}`} className="h-24 rounded object-cover" />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} data-testid="update-log-btn">
              {uploading ? 'Updating...' : 'Update Log'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMaintenanceDialog;
